'use client';

import { createClient } from '@/lib/supabase/client';

export interface ProjectRow {
  id: string;
  slug: string;
  developer_name: string;
  project_name: string;
  launch_date: string;
  location: string;
  micro_market: string;
  project_type: string;
  total_land_parcel: string;
  total_units: number;
  launched_units: number;
  sold_units: number;
  inventory_sold_pct: number;
  carpet_area_min: number;
  carpet_area_max: number;
  agreement_value_min: number;
  agreement_value_max: number;
  possession_date: string;
  rera_number: string;
  towers: number;
  floors: number;
  units_per_floor: number;
  lift_per_floor: number;
  project_status: string;
  price_per_sqft: number;
  market_avg_price_per_sqft: number;
  image_url: string;
  google_maps_url: string;
  amenities: string[];
  contact_details: string;
  litigation_details: string;
  payment_plan: string;
  stage_of_construction: string;
  payment_due: string;
  maintenance_charges: string;
  nearby_landmarks: NearbyLandmark[];
}

export interface NearbyLandmark {
  id: string;
  category: string;
  name: string;
  distance: string;
}

export interface ProjectImage {
  id: string;
  project_id: string;
  image_category: string;
  src: string;
  alt: string;
  label: string;
  sort_order: number;
}

export interface UnitConfiguration {
  id: string;
  project_id: string;
  unit_type: string;
  tower: string;
  carpet_area: number;
  built_up_area: number;
  agreement_value: number;
  package_cost: number;
  floor_range: string;
  available_units: number;
  total_units: number;
}

export interface TowerData {
  id: string;
  project_id: string;
  tower_name: string;
  floors: number;
  total_units: number;
  sold_units: number;
  unit_type: string;
  tower_status: string;
}

export interface PriceHistory {
  id: string;
  project_id: string;
  period: string;
  price_per_sqft: number;
  market_avg: number;
}

export interface AbsorptionHistory {
  id: string;
  project_id: string;
  period: string;
  sold_units: number;
  launched_units: number;
}

export interface ProjectPhase {
  id: string;
  project_id: string;
  phase_number: number;
  phase_name: string;
  rera_number: string;
  total_units: number;
  launched_units: number;
  sold_units: number;
  inventory_sold_pct: number;
  carpet_area_min: number;
  carpet_area_max: number;
  agreement_value_min: number;
  agreement_value_max: number;
  possession_date: string;
  launch_date: string;
  towers: number;
  floors: number;
  units_per_floor: number;
  price_per_sqft: number;
  phase_status: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

export const projectService = {
  async getAllProjects(): Promise<ProjectRow[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('inventory_sold_pct', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Projects fetch error:', error.message);
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error fetching projects:', err.message);
      throw err;
    }
  },

  async getProjectBySlug(slug: string): Promise<ProjectRow | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Project fetch error:', error.message);
        return null;
      }
      return data;
    } catch (err: any) {
      console.log('Schema error fetching project:', err.message);
      throw err;
    }
  },

  async getProjectImages(projectId: string): Promise<ProjectImage[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('project_images')
        .select('*')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error fetching images:', err.message);
      return [];
    }
  },

  async getUnitConfigurations(projectId: string): Promise<UnitConfiguration[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('unit_configurations')
        .select('*')
        .eq('project_id', projectId);

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error fetching unit configs:', err.message);
      return [];
    }
  },

  async getTowerData(projectId: string): Promise<TowerData[]> {
    const supabase = createClient();
    try {
      const [towerRes, inventoryRes] = await Promise.all([
        supabase
          .from('tower_data')
          .select('*')
          .eq('project_id', projectId),
        supabase
          .from('unit_inventory')
          .select('tower_id, status')
          .eq('project_id', projectId),
      ]);

      if (towerRes.error) {
        if (isSchemaError(towerRes.error)) throw towerRes.error;
        return [];
      }

      const towers = towerRes.data || [];
      const inventory = inventoryRes.data || [];

      // Build a map of tower_id -> sold count from unit_inventory
      const soldCountMap: Record<string, number> = {};
      const totalCountMap: Record<string, number> = {};
      for (const unit of inventory) {
        if (!unit.tower_id) continue;
        totalCountMap[unit.tower_id] = (totalCountMap[unit.tower_id] || 0) + 1;
        if (unit.status === 'sold') {
          soldCountMap[unit.tower_id] = (soldCountMap[unit.tower_id] || 0) + 1;
        }
      }

      // Override sold_units (and optionally total_units) with live inventory counts
      return towers.map((tower: any) => {
        const liveSold = soldCountMap[tower.id];
        const liveTotal = totalCountMap[tower.id];
        return {
          ...tower,
          sold_units: liveSold !== undefined ? liveSold : (tower.sold_units ?? 0),
          total_units: liveTotal !== undefined ? liveTotal : (tower.total_units ?? 0),
        };
      });
    } catch (err: any) {
      console.log('Schema error fetching tower data:', err.message);
      return [];
    }
  },

  async getPriceHistory(projectId: string): Promise<PriceHistory[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('price_history')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error fetching price history:', err.message);
      return [];
    }
  },

  async getAbsorptionHistory(projectId: string): Promise<AbsorptionHistory[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('absorption_history')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error fetching absorption history:', err.message);
      return [];
    }
  },

  async getProjectPhases(projectId: string): Promise<ProjectPhase[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('project_phases')
        .select('*')
        .eq('project_id', projectId)
        .order('phase_number', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error fetching project phases:', err.message);
      return [];
    }
  },

  async getUserSubscription(userId: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (err: any) {
      console.log('Schema error fetching subscription:', err.message);
      return null;
    }
  },
};
